import React, { useEffect, useState } from 'react';
import {
    Box,
    CardContent,
    Typography,
    TextField,
    Button,
    Alert,
    Checkbox,
    FormControlLabel,
    Grid,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    IconButton
} from '@mui/material';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { IconBuilding, IconDeviceFloppy, IconTrash, IconPlus, IconEdit, IconBuildingSkyscraper } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';
import GradientIconBadge from '../../../../../ui-component/extended/GradientIconBadge';

const FIELDS = [
    { name: 'name', label: 'Nome Fantasia', required: true },
    { name: 'phone', label: 'Telefone' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'address', label: 'Endereço' },
    { name: 'description', label: 'Descrição' }
];

const EditOrganization = () => {
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    const isSupplierMode = new URLSearchParams(location.search).get('type') === 'supplier';

    const [organization, setOrganization] = useState({
        name: '',
        description: '',
        cnpj: '',
        email: '',
        phone: '',
        address: '',
        is_customer: true,
        is_supplier: true,
        person_type: 'pj',
        country: '',
        default_financial_category: '',
        default_cost_center: '',
        default_payment_method: '',
        default_payment_term: '',
        state_registration: '',
        state_registration_exempt: false,
        municipal_registration: '',
        default_invoice_type: '',
        withholds_iss: false,
        withholds_federal_taxes: false
    });

    const [establishments, setEstablishments] = useState([]);
    const [financialCategories, setFinancialCategories] = useState([]);
    const [costCenters, setCostCenters] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [saving, setSaving] = useState(false);

    const pegar_app_padrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            const data = response.data;

            if (data?.app?.id) {
                setAppId(data.app.id);
            } else {
                setStatus({ type: 'error', message: 'App padrão não encontrado' });
            }
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
        }
    };

    useEffect(() => {
        if (!localStorage.getItem('access_token')) return;
        pegar_app_padrao();
    }, []);

    useEffect(() => {
        if (!appId || !id) return;
        fetchOrganization();
        fetchEstablishments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, id]);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            try {
                const [fc, cc, pm, pt, co] = await Promise.all([
                    api.get(`v1/apps/${appId}/financial-categories/?type=expense`),
                    api.get(`v1/apps/${appId}/cost-centers/`),
                    api.get(`v1/apps/${appId}/payment-methods/`),
                    api.get(`v1/apps/${appId}/payment-terms/`),
                    api.get(`v1/apps/${appId}/countries/`)
                ]);
                setFinancialCategories(fc.data || []);
                setCostCenters(cc.data || []);
                setPaymentMethods(pm.data || []);
                setPaymentTerms(pt.data || []);
                setCountries(co.data || []);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [appId]);

    const selectedCountry = countries.find((c) => c.id === organization.country);
    const isForeign = !!selectedCountry && selectedCountry.code !== 'BR';

    const fetchOrganization = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/organizations/${id}/`);

            if (response.data?.organization) {
                setOrganization(response.data.organization);
            }

            setLoading(false);
        } catch (error) {
            console.error('Erro ao buscar organização:', error);
            setLoading(false);
        }
    };

    const fetchEstablishments = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/establishments/?organization=${id}`);
            setEstablishments(response.data.establishments || []);
        } catch (error) {
            console.error('Erro ao buscar unidades:', error);
        }
    };

    const handleChange = (e) => {
        setOrganization({
            ...organization,
            [e.target.name]: e.target.value
        });
    };

    const handleCheckboxChange = (e) => {
        setOrganization({
            ...organization,
            [e.target.name]: e.target.checked
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!appId) return;

        setStatus({ type: '', message: '' });
        setSaving(true);

        try {
            const payload = {
                ...organization,
                country: organization.country || null,
                default_financial_category: organization.default_financial_category || null,
                default_cost_center: organization.default_cost_center || null,
                default_payment_method: organization.default_payment_method || null,
                default_payment_term: organization.default_payment_term || null,
                default_invoice_type: organization.default_invoice_type || null
            };
            await api.patch(`v1/apps/${appId}/organizations/${id}/`, payload);

            setStatus({ type: 'success', message: 'Empresa atualizada com sucesso!' });
        } catch (error) {
            console.error(error);
            setStatus({
                type: 'error',
                message: error.response?.data?.detail || error.response?.data?.error || 'Erro ao atualizar empresa.'
            });
        } finally {
            setSaving(false);
        }
    };

    const deleteOrganization = async () => {
        if (!appId) return;
        if (!window.confirm(`Excluir a empresa "${organization.name}"? Isso também remove suas unidades vinculadas.`)) return;

        try {
            await api.delete(`v1/apps/${appId}/organizations/${id}/`);
            history.push(isSupplierMode ? '/dashboard/config/suppliers' : '/dashboard/config/organizations/view');
        } catch (error) {
            console.error('Erro ao deletar organização:', error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao excluir empresa.' });
        }
    };

    const handleNewEstablishment = () => {
        history.push('/dashboard/establishments/new', { organization: { id, name: organization.name } });
    };

    const handleEditEstablishment = (establishmentId) => {
        history.push(`/dashboard/establishments/${establishmentId}/edit`, { organization: { id, name: organization.name } });
    };

    const handleDeleteEstablishment = async (establishment) => {
        if (!window.confirm(`Excluir a unidade "${establishment.name}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/establishments/${establishment.id}/`);
            setEstablishments((prev) => prev.filter((e) => e.id !== establishment.id));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir unidade.' });
        }
    };

    const backPage = () => {
        history.push(isSupplierMode ? '/dashboard/config/suppliers' : '/dashboard/config/organizations/view');
    };

    if (loading) return null;

    return (
        <Box>
            <PageHeader
                icon={IconBuilding}
                gradient="blue"
                title={organization.name || 'Empresa'}
                subtitle={organization.cnpj || 'Editar dados da empresa'}
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <GlassCard sx={{ mb: 3 }}>
                <CardContent>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            {FIELDS.map((field) => (
                                <Grid item xs={12} sm={6} key={field.name}>
                                    <TextField
                                        {...field}
                                        fullWidth
                                        value={organization[field.name] || ''}
                                        onChange={handleChange}
                                        variant="outlined"
                                    />
                                </Grid>
                            ))}

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="País"
                                    fullWidth
                                    value={organization.country || ''}
                                    onChange={(e) => setOrganization({ ...organization, country: e.target.value })}
                                    helperText="Deixe em branco pra Brasil"
                                >
                                    <MenuItem value="">Brasil</MenuItem>
                                    {countries.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="cnpj"
                                    label={isForeign ? 'Tax ID / EIN' : 'CNPJ/CPF'}
                                    placeholder={isForeign ? 'Número de identificação fiscal no país de origem' : ''}
                                    fullWidth
                                    value={organization.cnpj || ''}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="person_type"
                                    select
                                    label="Tipo de Pessoa"
                                    fullWidth
                                    value={organization.person_type || 'pj'}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="pj">Pessoa Jurídica</MenuItem>
                                    <MenuItem value="pf">Pessoa Física</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <Stack direction="row" spacing={3}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="is_customer"
                                                checked={!!organization.is_customer}
                                                onChange={handleCheckboxChange}
                                            />
                                        }
                                        label="É cliente"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="is_supplier"
                                                checked={!!organization.is_supplier}
                                                onChange={handleCheckboxChange}
                                            />
                                        }
                                        label="É fornecedor"
                                    />
                                </Stack>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </GlassCard>

            <GlassCard sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                        Classificação Financeira e Fiscal
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Categoria Financeira padrão"
                                    fullWidth
                                    value={organization.default_financial_category || ''}
                                    onChange={(e) => setOrganization({ ...organization, default_financial_category: e.target.value })}
                                    helperText="Usada pra classificar as contas a pagar geradas pra esse cadastro"
                                >
                                    <MenuItem value="">Nenhuma</MenuItem>
                                    {financialCategories.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Centro de Custo padrão"
                                    fullWidth
                                    value={organization.default_cost_center || ''}
                                    onChange={(e) => setOrganization({ ...organization, default_cost_center: e.target.value })}
                                >
                                    <MenuItem value="">Nenhum</MenuItem>
                                    {costCenters.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Forma de Pagamento padrão"
                                    fullWidth
                                    value={organization.default_payment_method || ''}
                                    onChange={(e) => setOrganization({ ...organization, default_payment_method: e.target.value })}
                                >
                                    <MenuItem value="">Nenhuma</MenuItem>
                                    {paymentMethods.map((m) => (
                                        <MenuItem key={m.id} value={m.id}>
                                            {m.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Condição de Pagamento padrão"
                                    fullWidth
                                    value={organization.default_payment_term || ''}
                                    onChange={(e) => setOrganization({ ...organization, default_payment_term: e.target.value })}
                                >
                                    <MenuItem value="">Nenhuma</MenuItem>
                                    {paymentTerms.map((t) => (
                                        <MenuItem key={t.id} value={t.id}>
                                            {t.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {!isForeign && (
                                <>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            name="state_registration"
                                            label="Inscrição Estadual"
                                            fullWidth
                                            disabled={!!organization.state_registration_exempt}
                                            value={organization.state_registration || ''}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2} display="flex" alignItems="center">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="state_registration_exempt"
                                                    checked={!!organization.state_registration_exempt}
                                                    onChange={handleCheckboxChange}
                                                />
                                            }
                                            label="Isento de IE"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="municipal_registration"
                                            label="Inscrição Municipal"
                                            fullWidth
                                            value={organization.municipal_registration || ''}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="default_invoice_type"
                                    select
                                    label="Tipo de Nota Fiscal padrão"
                                    fullWidth
                                    value={organization.default_invoice_type || ''}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="">Não definido</MenuItem>
                                    <MenuItem value="nfse">NFS-e (Nota Fiscal de Serviço Eletrônica)</MenuItem>
                                    <MenuItem value="invoice">Fatura/Invoice (contratação internacional)</MenuItem>
                                </TextField>
                            </Grid>

                            {!isForeign && (
                                <Grid item xs={12} sm={6}>
                                    <Stack direction="row" spacing={3} alignItems="center" height="100%">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="withholds_iss"
                                                    checked={!!organization.withholds_iss}
                                                    onChange={handleCheckboxChange}
                                                />
                                            }
                                            label="Retém ISS na fonte"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="withholds_federal_taxes"
                                                    checked={!!organization.withholds_federal_taxes}
                                                    onChange={handleCheckboxChange}
                                                />
                                            }
                                            label="Retém PIS/COFINS/CSLL/IRRF na fonte"
                                        />
                                    </Stack>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                                    <Button color="error" onClick={deleteOrganization} startIcon={<IconTrash size={18} />}>
                                        Excluir empresa
                                    </Button>
                                    <Button onClick={backPage}>Voltar</Button>
                                    <Button type="submit" variant="contained" startIcon={<IconDeviceFloppy size={18} />} disabled={saving}>
                                        {saving ? 'Salvando...' : 'Salvar'}
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </GlassCard>

            <GlassCard>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <GradientIconBadge icon={IconBuildingSkyscraper} gradient="teal" size={38} iconSize={18} />
                            <Typography variant="h6" fontWeight={700}>
                                Unidades / Filiais
                            </Typography>
                        </Stack>
                        <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleNewEstablishment}>
                            Nova unidade
                        </Button>
                    </Stack>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Código</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>CNPJ</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {establishments.map((establishment) => (
                                <TableRow key={establishment.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleEditEstablishment(establishment.id)}>
                                    <TableCell>{establishment.code}</TableCell>
                                    <TableCell>{establishment.name}</TableCell>
                                    <TableCell>{establishment.cnpj || '-'}</TableCell>
                                    <TableCell>
                                        {establishment.is_matriz ? (
                                            <Chip label="Matriz" color="primary" size="small" />
                                        ) : (
                                            <Chip label="Filial" size="small" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                        <IconButton size="small" onClick={() => handleEditEstablishment(establishment.id)}>
                                            <IconEdit size={16} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteEstablishment(establishment)}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {establishments.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Nenhuma unidade cadastrada para essa empresa ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>
        </Box>
    );
};

export default EditOrganization;
